import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertOctagon,
  CheckSquare,
  ClipboardList,
  Eye,
  Radio,
  Search,
  Trophy,
  Users,
  X,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime, formatNumber } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { Avatar } from '../../components/Avatar'
import { useAuthStore } from '../../stores/authStore'
import { teamResponse } from '../../lib/types'
import {
  PeriodToggle,
  useDashboardPeriod,
} from '../../components/ui/PeriodToggle'

// `initials()` helper removed — the shared `Avatar` component now
// renders the colored circle for every team member row, with the
// brand ocean→cyan gradient (same as the UserMenu chip and every
// other avatar across the app). It handles the initials derivation
// internally, plus the profile-image fallback when the user has
// uploaded one. See `components/Avatar.jsx`.

/** Presence sub-label for a member row. Online → "Online now"; otherwise the
 *  relative last-active time ("Active 16 hours ago"), or "Never signed in".
 *  Uses Luxon (the repo date standard) so wording matches the rest of the app. */
function presenceLabel(member) {
  if (member.isOnline) return 'Online now'
  if (!member.lastActiveAt) return 'Never signed in'
  const rel = DateTime.fromISO(member.lastActiveAt).toRelative()
  return rel ? `Active ${rel}` : 'Active recently'
}

const WORK_ROLE_LABEL = {
  manager: 'Manager',
  team_leader: 'Team Leader',
  employee: 'Employee',
}

// A member's displayed role is its Work Role NAME (operations-driven — the
// inert `workRole` tier slug is 'employee' for every custom role). This is the
// single label used both in the table and as the filter key, so the Work Role
// filter lists the ACTUAL roles in the team (SOP Process Head, Sr. SOP
// Specialist, …) rather than the three legacy Manager / TL / Employee tiers.
const NO_ROLE = 'No Work Role'

function roleKey(m) {
  return m.workRoleName?.trim() || NO_ROLE
}
function roleLabel(m) {
  return m.workRoleName ?? WORK_ROLE_LABEL[m.workRole] ?? 'Employee'
}

/** Team Overview — per-member supervision view for Managers / Team
 *  Leaders / Company Admins. Shows everyone in scope with their
 *  workspace + department placements and a compact stats strip
 *  (open tasks, overdue, reports submitted, points, time logged).
 *  Sidebar surfaces this as "Team Overview". */
export function WorkspaceOverviewPage() {
  const user = useAuthStore((s) => s.user)
  const [search, setSearch] = useState('')
  const [workRoleFilter, setWorkRoleFilter] = useState('all')
  const [deptFilter, setDeptFilter] = useState('all')
  const [period, setPeriod] = useDashboardPeriod()

  const teamQuery = useQuery({
    queryKey: ['team', period],
    queryFn: async () => {
      const res = await api.get('/me/team', { params: { period } })
      return teamResponse.parse(res.data)
    },
    refetchInterval: 60_000,
  })

  const data = useMemo(() => teamQuery.data?.data ?? [], [teamQuery.data])
  // [QA-18] Opened by URL without the access the API requires: show a role
  // panel (as Team Analytics does) instead of zeroed KPIs + a futile retry.
  const forbidden =
    teamQuery.isError && !teamQuery.data && teamQuery.error?.response?.status === 403
  // No data because the load failed: the summary cards would read 0 — hide
  // them and let the table carry the error + retry.
  const loadFailed = teamQuery.isError && !teamQuery.data

  // The Work Role filter options — every DISTINCT Work Role actually present in
  // the team roster, with its member count, sorted alphabetically. Driven by
  // the real roles (operations-driven) so a manager filters by "Sr. SOP
  // Specialist" etc., not the retired Manager / Team Leader / Employee tiers.
  const roleOptions = useMemo(() => {
    const byName = new Map()
    for (const m of data) {
      const key = roleKey(m)
      byName.set(key, (byName.get(key) ?? 0) + 1)
    }
    return [...byName.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [data])

  // Show the Department column + filter only when the in-scope roster actually
  // spans MORE THAN ONE department (a company-wide viewer with a multi-dept
  // company). Keyed on departmentName (null → a shared "none" bucket), read
  // from the FULL in-scope `data` so narrowing by search / role never flips
  // the column.
  const showDepartmentColumn = useMemo(() => {
    const keys = new Set()
    for (const m of data ?? []) {
      for (const p of m.placements ?? []) {
        keys.add(p.departmentName ?? '__none__')
        if (keys.size > 1) return true
      }
    }
    return keys.size > 1
  }, [data])

  // Department filter options — every DISTINCT department present in the roster
  // (null → a "No department" bucket), with member counts. Only surfaced when
  // the Department column is shown.
  const NONE_DEPT = '__none__'
  const deptOptions = useMemo(() => {
    const byDept = new Map()
    for (const m of data) {
      const names = new Set(
        (m.placements ?? []).map((p) => p.departmentName ?? NONE_DEPT),
      )
      if (names.size === 0) names.add(NONE_DEPT)
      for (const n of names) byDept.set(n, (byDept.get(n) ?? 0) + 1)
    }
    return [...byDept.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => {
        if (a.value === NONE_DEPT) return 1 // "No department" sorts last
        if (b.value === NONE_DEPT) return -1
        return a.value.localeCompare(b.value)
      })
  }, [data])

  // Page-owned filtering — the KPI summary below reads `filtered`, so search /
  // Work Role / Department stay controlled here (not inside DataTable). The
  // DataTable then sorts, paginates, and exports the pre-filtered rows.
  const filtered = useMemo(() => {
    const trimmed = search.trim().toLowerCase()
    return data.filter((m) => {
      if (workRoleFilter !== 'all' && roleKey(m) !== workRoleFilter) return false
      if (showDepartmentColumn && deptFilter !== 'all') {
        const inDept = (m.placements ?? []).some(
          (p) => (p.departmentName ?? NONE_DEPT) === deptFilter,
        )
        if (!inDept) return false
      }
      if (!trimmed) return true
      return (
        m.fullName.toLowerCase().includes(trimmed) ||
        m.emailId.toLowerCase().includes(trimmed) ||
        (showDepartmentColumn &&
          m.placements.some((p) =>
            (p.departmentName ?? '').toLowerCase().includes(trimmed),
          ))
      )
    })
  }, [data, search, workRoleFilter, deptFilter, showDepartmentColumn])

  const summary = useMemo(() => {
    // Roster facts summarised over the currently VISIBLE members, so People /
    // Online / Behind on Reports always reconcile with the table below.
    let pendingReports = 0
    let onlineNow = 0
    let openTasksSum = 0
    let overdueSum = 0
    for (const m of filtered) {
      openTasksSum += m.stats.openTasks
      overdueSum += m.stats.overdueTasks
      if (m.stats.reportsLast30 === 0) pendingReports += 1
      if (m.isOnline) onlineNow += 1
    }

    // TO DO / Overdue cards — DISTINCT task counts using the same first-column
    // TO DO definition regardless of the filter (see [[reference_team_overview_kpi_and_role_filter]]).
    const feed = teamQuery.data?.summary
    const isWholeTeam = workRoleFilter === 'all' && search.trim() === ''
    const facts = feed?.openTaskFacts
    let openTasks
    let overdue
    if (isWholeTeam) {
      openTasks = feed?.openTaskCount ?? openTasksSum
      overdue = feed?.overdueTaskCount ?? overdueSum
    } else if (facts) {
      const visible = new Set(filtered.map((m) => String(m.userId)))
      const inScope = (f) => f.assigneeIds.some((id) => visible.has(String(id)))
      openTasks = facts.filter((f) => f.todo && inScope(f)).length
      overdue = facts.filter((f) => f.overdue && inScope(f)).length
    } else {
      openTasks = openTasksSum
      overdue = overdueSum
    }
    return {
      people: filtered.length,
      openTasks,
      overdue,
      pendingReports,
      onlineNow,
      isWholeTeam,
    }
  }, [filtered, teamQuery.data, workRoleFilter, search])

  // Guard against a selected Work Role that's no longer present (its members
  // dropped out after a refetch, or the role was removed). Fall back to "All".
  useEffect(() => {
    if (
      workRoleFilter !== 'all' &&
      !roleOptions.some((o) => o.value === workRoleFilter)
    ) {
      setWorkRoleFilter('all')
    }
  }, [roleOptions, workRoleFilter])

  // Same guard for the Department filter — reset to "All" if the selected
  // department drops out of the roster or the column is hidden.
  useEffect(() => {
    if (
      deptFilter !== 'all' &&
      (!showDepartmentColumn || !deptOptions.some((o) => o.value === deptFilter))
    ) {
      setDeptFilter('all')
    }
  }, [deptOptions, deptFilter, showDepartmentColumn])

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'member',
        header: 'Member',
        grow: true,
        pinnable: true,
        minWidth: 240,
        sortAccessor: (m) => (m.fullName ?? '').toLowerCase(),
        exportValue: (m) => `${m.fullName} <${m.emailId}>`,
        render: (m) => (
          <div className="flex items-center gap-3 min-w-[220px]">
            {/* Avatar with a live-presence badge — green when active (last
             *  15 min), muted otherwise. */}
            <div className="relative shrink-0">
              <Avatar name={m.fullName} avatarUrl={m.avatarUrl ?? null} size="md" />
              <span
                title={presenceLabel(m)}
                aria-label={m.isOnline ? 'Online' : 'Offline'}
                className={
                  'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[color:var(--color-card)] ' +
                  (m.isOnline
                    ? 'bg-[color:var(--color-success)]'
                    : 'bg-[color:var(--color-ink-subtle)]')
                }
              />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-[color:var(--color-ink-strong)] truncate">
                {m.fullName}
              </p>
              <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
                {m.emailId}
              </p>
              <p
                className={
                  'text-xs truncate ' +
                  (m.isOnline
                    ? 'text-[color:var(--color-success)] font-medium'
                    : 'text-[color:var(--color-ink-subtle)]')
                }
              >
                {presenceLabel(m)}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: 'workRole',
        header: 'Work Role',
        minWidth: 140,
        sortAccessor: (m) => roleKey(m).toLowerCase(),
        exportValue: (m) => roleLabel(m),
        render: (m) => (
          <span className="text-sm text-[color:var(--color-ink)] whitespace-nowrap">
            {roleLabel(m)}
          </span>
        ),
      },
    ]
    if (showDepartmentColumn) {
      cols.push({
        key: 'department',
        header: 'Department',
        minWidth: 160,
        sortAccessor: (m) => (m.placements?.[0]?.departmentName ?? '').toLowerCase(),
        exportValue: (m) =>
          (m.placements ?? [])
            .map((p) => p.departmentName ?? 'No department')
            .join(', '),
        render: (m) =>
          m.placements.length === 0 ? (
            <span className="text-sm text-[color:var(--color-ink-subtle)]">—</span>
          ) : (
            <div className="flex flex-col gap-0.5 min-w-[120px] max-w-[220px]">
              {m.placements.slice(0, 3).map((p) => (
                <span
                  key={`${m.userId}-${p.workspaceId}`}
                  className="text-sm text-[color:var(--color-ink)] truncate"
                >
                  {p.departmentName ? (
                    <span className="text-[color:var(--color-ink-strong)] font-medium">
                      {p.departmentName}
                    </span>
                  ) : (
                    <span className="text-[color:var(--color-ink-subtle)]">
                      No department
                    </span>
                  )}
                </span>
              ))}
              {m.placements.length > 3 ? (
                <span className="text-sm text-[color:var(--color-ink-subtle)]">
                  +{m.placements.length - 3} more
                </span>
              ) : null}
            </div>
          ),
      })
    }
    cols.push(
      {
        key: 'open',
        // [QA-21] First-column ("To Do") count — the same basis as the "To Do"
        // card above and the dashboard's "To Do" tile. It was headed "Open",
        // while the dashboard uses "open" for every not-completed task.
        header: 'To Do',
        align: 'center',
        minWidth: 80,
        sortAccessor: (m) => m.stats.openTasks,
        exportValue: (m) => m.stats.openTasks,
        render: (m) => <span className="tabular-nums">{m.stats.openTasks}</span>,
      },
      {
        key: 'overdue',
        header: 'Overdue',
        align: 'center',
        minWidth: 90,
        sortAccessor: (m) => m.stats.overdueTasks,
        exportValue: (m) => m.stats.overdueTasks,
        render: (m) => (
          <span
            className={
              'tabular-nums ' +
              (m.stats.overdueTasks > 0
                ? 'text-[color:var(--color-priority-urgent)] font-semibold'
                : '')
            }
          >
            {m.stats.overdueTasks}
          </span>
        ),
      },
      {
        key: 'done',
        header: 'Done (all time)',
        align: 'center',
        minWidth: 120,
        sortAccessor: (m) => m.stats.completed,
        exportValue: (m) => m.stats.completed,
        render: (m) => <span className="tabular-nums">{m.stats.completed}</span>,
      },
      {
        key: 'reports',
        header: 'Reports',
        align: 'center',
        minWidth: 90,
        sortAccessor: (m) => m.stats.reportsLast30,
        exportValue: (m) => m.stats.reportsLast30,
        render: (m) => (
          <span
            className={
              'tabular-nums ' +
              (m.stats.reportsLast30 === 0
                ? 'text-[color:var(--color-warning)] font-semibold'
                : '')
            }
          >
            {m.stats.reportsLast30}
          </span>
        ),
      },
      {
        key: 'points',
        header: 'Points',
        align: 'center',
        minWidth: 90,
        sortAccessor: (m) => m.stats.pointsLast30,
        exportValue: (m) => m.stats.pointsLast30,
        render: (m) => (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Trophy size={12} className="text-[color:var(--color-accent-strong)]" />
            {formatNumber(m.stats.pointsLast30)}
          </span>
        ),
      },
      {
        key: 'lastActive',
        header: 'Last Active',
        minWidth: 160,
        sortAccessor: (m) => (m.lastActiveAt ? new Date(m.lastActiveAt) : null),
        exportValue: (m) =>
          m.lastActiveAt ? formatDateTime(new Date(m.lastActiveAt)) : '',
        render: (m) => (
          <span className="text-sm text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap">
            {m.lastActiveAt ? formatDateTime(new Date(m.lastActiveAt)) : '—'}
          </span>
        ),
      },
    )
    return cols
  }, [showDepartmentColumn])

  return (
    <div>
      <Breadcrumb current="Team Overview" homePath="/app" homeLabel="Dashboard" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            {/* Same icon as this route’s sidebar entry (UserLayout “Team Overview”).
                The heading previously used Users while the sidebar used Eye, so
                the page and the nav item it came from looked unrelated
                (defect DF_029 / Mantis 22222). */}
            <Eye size={20} className="text-[color:var(--color-accent-strong)]" />
            Team Overview
          </h1>
          <p className="mt-1 text-[color:var(--color-ink-muted)]">
            {user?.fullName?.split(/\s+/)[0]
              ? `${user.fullName.split(/\s+/)[0]}, see your team.`
              : 'See your team.'}
          </p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      {forbidden ? (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center">
          <Users size={26} className="mx-auto mb-3 text-[color:var(--color-ink-muted)]" />
          <p className="text-sm text-[color:var(--color-ink-strong)] font-medium m-0">
            Team Overview isn&apos;t available for your role.
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {extractApiError(teamQuery.error).message}
          </p>
        </div>
      ) : (
      <>
      {/* Headline counts — just enough numbers to frame the table below.
       *  Hidden when the load failed with no data: they would all read 0. */}
      {loadFailed ? null : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <SummaryCard
          icon={Users}
          label="Members"
          value={summary.people}
          tone="violet"
        />
        <SummaryCard
          icon={Radio}
          label="Online Now"
          value={summary.onlineNow}
          tone={summary.onlineNow > 0 ? 'emerald' : 'slate'}
        />
        <SummaryCard
          icon={CheckSquare}
          label="To Do"
          value={summary.openTasks}
          hint={
            summary.isWholeTeam
              ? 'First-column tasks, incl. unassigned'
              : 'First-column tasks for the filtered members'
          }
          tone="sky"
        />
        <SummaryCard
          icon={AlertOctagon}
          label="Overdue Tasks & Subtasks"
          value={summary.overdue}
          hint={
            summary.isWholeTeam
              ? 'All team tasks, incl. unassigned'
              : 'Overdue tasks for the filtered members'
          }
          tone={summary.overdue > 0 ? 'rose' : 'emerald'}
        />
        <SummaryCard
          icon={ClipboardList}
          label="Behind on Reports"
          value={summary.pendingReports}
          tone={summary.pendingReports > 0 ? 'amber' : 'emerald'}
        />
      </div>
      )}

      {/* Filters — search + Work Role + Department stay page-owned so the KPI
       *  cards above always reconcile with the visible roster. */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative w-full sm:w-[26rem] max-w-full">
          <span
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
          >
            <Search size={15} />
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              showDepartmentColumn
                ? 'Search by Name, Email ID or Department'
                : 'Search by Name or Email ID'
            }
            className={
              'fd-search-pill w-full h-10 pl-9 rounded-full text-sm text-[color:var(--color-ink)] outline-none ' +
              (search ? 'pr-9' : 'pr-4')
            }
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              title="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <X size={12} />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="team-role-filter"
            className="text-sm text-[color:var(--color-ink-muted)] whitespace-nowrap"
          >
            Work Role
          </label>
          <select
            id="team-role-filter"
            value={workRoleFilter}
            onChange={(e) => setWorkRoleFilter(e.target.value)}
            className="h-10 max-w-[240px] rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
          >
            <option value="all">All Work Roles ({data.length})</option>
            {roleOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.value} ({o.count})
              </option>
            ))}
          </select>
        </div>
        {showDepartmentColumn ? (
          <div className="flex items-center gap-2">
            <label
              htmlFor="team-dept-filter"
              className="text-sm text-[color:var(--color-ink-muted)] whitespace-nowrap"
            >
              Department
            </label>
            <select
              id="team-dept-filter"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="h-10 max-w-[240px] rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
            >
              <option value="all">All Departments ({data.length})</option>
              {deptOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.value === NONE_DEPT ? 'No department' : o.value} ({o.count})
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <DataTable
        tableId="employee-team-overview"
        columns={columns}
        rows={filtered}
        getRowId={(m) => m.userId}
        loading={teamQuery.isLoading}
        // [QA-14] Blocking only without data — a failed 60 s background
        // refetch keeps the last good roster on screen.
        error={loadFailed ? extractApiError(teamQuery.error).message : null}
        onRetry={() => teamQuery.refetch()}
        searchable={false}
        title="Team Overview"
        exportBaseName="team-overview"
        exportTitle="Team Overview"
        emptyState={{
          icon: Users,
          title:
            data.length === 0
              ? 'No team members in scope yet'
              : 'No teammates match your filters',
          body:
            data.length === 0
              ? 'Add people to your Workspaces to populate this list.'
              : undefined,
        }}
      />
      </>
      )}
    </div>
  )
}

const SUMMARY_TONE = {
  violet: 'bg-[#8b5cf6]/15 text-[#8b5cf6] border border-[#8b5cf6]/30',
  sky: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
  rose: 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]',
  amber:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  emerald:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  slate:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
}

function SummaryCard({ icon: Icon, label, value, hint, tone }) {
  // Sized to match the Company Admin DashboardPage KPI tiles —
  // h-11 w-11 icon container + size 20 lucide glyph, text-3xl value,
  // text-xs label. Keeps the two pages visually consistent.
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {hint ? (
            // Wrap (no `truncate`) so the full hint — e.g. "First-column tasks,
            // incl. unassigned" — is always visible instead of being clipped
            // to "…incl. un…". The card has no fixed height, so it just grows.
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] leading-snug break-words">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            (SUMMARY_TONE[tone] ?? SUMMARY_TONE.sky)
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

